import { useState } from "react";
import TimelineUpdate from "@/features/shipment-management/components/TimelineUpdate";
import { useShippingProgress } from "@/features/shipment-management/hooks/useTrackingStatus";

interface ShipmentTimelineProps {
  listingId: string;
  filterType?: "vehicle" | "title";
}

export default function ShipmentTimeline({ listingId, filterType }: ShipmentTimelineProps) {
  const [showFull, setShowFull] = useState(false);

  const { data: progressData, isLoading } = useShippingProgress(listingId, filterType);

  const events = (progressData ?? []).map((event) => ({
    id: event.id,
    title: event.title,
    timestamp: new Date(event.updatedAt).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    comment: event.comment,
    transporterUpdate: event.transporterUpdate,
    user: event.user,
    progressType: event.progressType,
  }));

  const displayed = showFull ? events : events.slice(0, 5);

  if (isLoading) {
    return (
      <div className="flex gap-1.5 py-4 justify-center">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-[#2563EB] animate-bounce"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="text-[12px] text-[#98A2B3] text-center py-4">
        No timeline events yet
      </p>
    );
  }

  return (
    <div className="w-full">
      {displayed.map((event) => (
        <div key={event.id} className="mb-4">
          <TimelineUpdate
            type={event.progressType?.toLowerCase() === "title" ? "title" : "carrier"}
            title={event.title}
            description={event.comment}
            updateTime={event.timestamp}
            updatedBy={
              event.transporterUpdate
                ? "transporter"
                : `${event.user?.firstName ?? ""} ${event.user?.lastName ?? ""}`.trim()
            }
          />
        </div>
      ))}

      {events.length > 5 && !showFull && (
        <button
          onClick={() => setShowFull(true)}
          className="flex items-center gap-1.5 text-[12px] text-[#2563EB] font-semibold mx-auto pt-2"
        >
          View earlier history
          <svg width="12" height="7" viewBox="0 0 12 7" fill="none">
            <path
              d="M0.75 0.75L5.75 5.75L10.75 0.75"
              stroke="#1D4ED8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
